import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import embed, { Result } from 'vega-embed';

const DARK_CONFIG = {
  background: 'transparent',
  font: 'Manrope, sans-serif',
  title: { color: '#e5e7eb', fontSize: 12, fontWeight: 600 },
  axis: {
    labelColor: '#94a3b8',
    titleColor: '#94a3b8',
    gridColor: '#374151',
    domainColor: '#374151',
    tickColor: '#374151',
  },
  legend: { labelColor: '#94a3b8', titleColor: '#94a3b8' },
  header: { labelColor: '#94a3b8', titleColor: '#94a3b8' },
  mark: { color: '#7c3aed' },
  range: {
    category: ['#7c3aed', '#06b6d4', '#f59e0b', '#16a34a', '#ef4444', '#8b5cf6', '#0ea5e9'],
  },
  view: { stroke: 'transparent' },
};

class VegaErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function VantiVegaChartInner({ spec }: { spec: object }) {
  const ref = useRef<HTMLDivElement>(null);
  const viewRef = useRef<Result | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    let cancelled = false;

    const mergedSpec = {
      ...spec,
      config: { ...(spec as any).config, ...DARK_CONFIG },
    };

    embed(ref.current, mergedSpec as any, {
      actions: false,
      renderer: 'svg',
      width: 'container' as any,
    })
      .then((result) => {
        if (cancelled) { result.view.finalize(); return; }
        viewRef.current = result;
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) { setError(true); setLoading(false); }
      });

    return () => {
      cancelled = true;
      viewRef.current?.view.finalize();
      viewRef.current = null;
    };
  }, [spec]);

  if (error) return null;

  return (
    <div className="relative min-h-[180px]">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="animate-pulse text-xs text-slate-500">Rendering chart…</span>
        </div>
      )}
      <div
        ref={ref}
        className={loading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}
        style={{ width: '100%' }}
      />
    </div>
  );
}

export default function VantiVegaChart({ spec }: { spec: object }) {
  return (
    <motion.div
      layout={false}
      className="my-3 rounded-xl border border-dark-border bg-dark-surface p-3"
    >
      <VegaErrorBoundary>
        <VantiVegaChartInner spec={spec} />
      </VegaErrorBoundary>
    </motion.div>
  );
}
